<template>
  <b-col class="m-auto">
    <slot name="logo"></slot>
    <h4 class="text-muted pb-4" v-if="!loading">{{ labels.finalizeTitle }}</h4>
    <div v-if="loading" class="loading">
      <loading />
      <p>{{ labels.loading }}</p>
    </div>
    <b-form v-else @submit.prevent="doConfirmInvite" ref="form">
      <CustomInput
        name="firstName"
        :label="labels.input.firstName"
        v-model="firstName"
        :disabled="true"
      ></CustomInput>

      <CustomInput
        name="lastName"
        :label="labels.input.lastName"
        v-model="lastName"
        :disabled="true"
      ></CustomInput>

      <CustomEmail
        :label="labels.input.email"
        :initialValue="email"
        :isValid="true"
        :disabled="true"
        inputId="email"
      />

      <b-form-group v-if="secretQuestions">
        <b-form-select
          id="secret-question-form-select"
          class="secret-question-select"
          size="lg"
          required
          @input="changeSecretQuestion($event)"
        >
          <option
            v-for="question in secretQuestions"
            :key="question.id"
            :value="question.id"
          >
            {{ question.label }}
          </option>
        </b-form-select>
        <label for="secret-question-form-select" class="custom-input-label">
          {{ labels.confirmAccount.secretQuestion }}
        </label>

        <label class="mdi mdi-chevron-down select-icon"></label>
      </b-form-group>

      <CustomInput
        v-if="secretQuestions"
        name="secret-question-answer"
        type="text"
        class="secret-question-answer"
        :label="labels.confirmAccount.secretQuestionAnswer"
        @update="updateConfirmInviteForm('secretQuestionAnswer', $event.value)"
      />

      <CustomInput
        v-if="!onlySSO"
        type="password"
        hasPasswordConfirm="true"
        :passwordRules="passwordRules"
        :title="labels.input.password"
        :label="labels.input.password"
        :labelWrong="labels.input.password"
        :confirmTitle="labels.input.passwordConfirmWarning"
        :confirmLabel="labels.input.passwordConfirm"
        :confirmLabelWrong="labels.input.passwordConfirmWrong"
        @update="updateConfirmInviteForm('password', $event.value)"
      ></CustomInput>

      <b-row v-if="confirmInvite.message" align-h="center">
        <span id="exception-message" class="text-secondary">{{
          confirmInvite.message
        }}</span>
      </b-row>

      <small class="font-weight-normal">
        {{ labels.termsAndPrivacy.partOne }}
        <strong>"{{ labels.register }}"</strong>
        {{ labels.termsAndPrivacy.partTwo }}
        <a :href="labels.termsAndPrivacy.link.privacy" target="_blank">
          {{ labels.termsAndPrivacy.partThree }}
        </a>
        {{ labels.termsAndPrivacy.partFour }}
        <a :href="labels.termsAndPrivacy.link.terms" target="_blank">
          {{ labels.termsAndPrivacy.partFive }}
        </a>
        .
      </small>

      <Recaptcha ref="recaptcha" @verify="recaptchaVerify">
        <b-button
          id="btn-invite"
          type="submit"
          :disabled="submitInProgress"
          variant="quartenary"
          class="col-12 mb-3 mt-3"
        >
          <i
            class="mdi mdi-spin pr-2"
            v-bind:class="{ 'mdi-loading': submitInProgress }"
          ></i>
          {{ labels.register }}
        </b-button>
      </Recaptcha>
    </b-form>
  </b-col>
</template>

<script>
import { mapGetters } from 'vuex'
import {
  getUser,
  isEnterprisePlan,
  isToNewLayout,
  isEarlyAccessTrialAccount,
} from '@/service/user'
import ability from '@/helpers/ability'
import CustomInput from '@/components/Shared/Form/CustomInput.vue'
import CustomEmail from '@/components/Shared/Form/CustomEmail.vue'
import Recaptcha from '@/components/Shared/Recaptcha.vue'
import Loading from '@/components/Shared/Loading.vue'
import auth from '@/mixins/auth'
import { AuthErrorCodes } from '@/helpers/authErrorCodes'

export default {
  components: {
    CustomInput,
    CustomEmail,
    Recaptcha,
    Loading,
  },

  data() {
    return {
      loading: false,
      passwordRules: undefined,
      secretQuestions: undefined,
      submitInProgress: false,
      firstName: this.$route.query.firstname,
      lastName: this.$route.query.lastname,
      email: this.normalizeEmail(this.$route.query.email),
      onlySSO: false,
    }
  },

  computed: {
    ...mapGetters({
      confirmInvite: 'confirmInvite',
      resources: 'resources',
    }),

    labels: function() {
      return {
        finalizeTitle: this.resources.page.login.new.confirmEmailPage
          .finalizeTitle,
        register: this.resources.page.login.new.confirmEmailPage.register,
        input: this.resources.general.input,
        loading: this.resources.page.login.new.confirmEmailPage.loading,
        termsAndPrivacy: this.resources.general.termsAndPrivacy,
        confirmAccount: this.resources.page.login.confirmAccount,
      }
    },

    canAccessNewProfile() {
      return (
        (isEnterprisePlan() || isEarlyAccessTrialAccount()) &&
        !this.$can('access', 'read-only')
      )
    },
  },

  mounted: function() {
    this.$nextTick(() => {
      this.$root.$emit('confirmationInvite', getUser()) // analytics

      const { token } = this.$route.params

      this.updateConfirmInviteForm('email', this.email)
      this.updateConfirmInviteForm('token', token)

      localStorage.setItem('redirectAfterLogin', this.$route.query.ref)

      this.loading = true
      this.$store
        .dispatch('checkConfirmed', token)
        .then(({ status }) => {
          // if user is confirmed
          if (status) {
            return this.$router.push({
              path: '/login',
              query: { ref: this.$route.query.ref },
            })
          }
          // if user is unconfirmed
          this.$store
            .dispatch('user/loadPasswordRules', {
              token,
              action: 'confirm-invite',
            })
            .then(async ({ data: { hasSecretQuestion, passwordRules } }) => {
              if (hasSecretQuestion) {
                const { data } = await this.$store.dispatch(
                  'user/loadSecretQuestions'
                )
                this.secretQuestions = data
              }

              this.passwordRules = passwordRules
              this.loading = false
            })
            .catch(error => {
              this.$store.commit('updateMessage', error.response.data.message)
              this.loading = false
            })
        })
        .catch(error => {
          const { code } = error.response.data

          const onlySSO = code === AuthErrorCodes.SSORedirect

          if (onlySSO) this.onlySSO = onlySSO
          else this.$store.commit('updateMessage', error.response.data.message)

          this.loading = false
        })
    })
  },

  methods: {
    changeSecretQuestion(value) {
      this.updateConfirmInviteForm('secretQuestion', value)
    },

    normalizeEmail: (email = '') => email.replace(' ', '+'),

    recaptchaVerify(value) {
      this.updateConfirmInviteForm('googleRecaptcha', value)
      this._doConfirmInvite()
    },

    updateConfirmInviteForm(property, value) {
      this.$store.commit('updateConfirmInviteForm', { property, value })
    },

    doConfirmInvite() {
      if (this.submitInProgress) return false

      if (!this.$refs.form.checkValidity()) {
        this.$refs.form.reportValidity()
        this.resetRecaptcha()
        return false
      }

      if (this.onlySSO) {
        window.location = `/login?code=${AuthErrorCodes.SSORedirect}`
        return false
      }

      this.$refs.recaptcha.execute()
    },

    _doConfirmInvite() {

      this.submitInProgress = true
      this.$store.commit('updateMessage', null)
      this.$store
        .dispatch('confirmInvite')
        .then(response => {
          this.submitInProgress = false
          this.$store.dispatch('user/saveUserData', response)
          this.$root.$emit('login', getUser())
          this.$ability.update(ability().rules)

          if (!this.$can('use', 'engagement')) {
            if (this.canAccessNewProfile && isToNewLayout('login-redirect')) {
              const redirect = '/dashboard?redirect=new-layout'
              window.location = redirect

              return
            }

            localStorage.setItem('redirectAfterLogin', '/dashboard')
          }

          auth.redirect()
        })
        .catch(error => {
          this.$store.commit('updateMessage', error.response.data.message)
          this.submitInProgress = false
        })
    },

    resetRecaptcha() {
      this.$refs.recaptcha.resetRecaptcha()
    },
  },
}
</script>

<style scoped lang="scss">
@import '~@/styles/custom/colors';

.loading {
  margin: 0 auto;
  text-align: center;
}

.secret-question-select {
  cursor: pointer;
}

.secret-question-answer {
  padding-top: 1.2rem !important;
}

/deep/ .custom-input-label {
  font-size: 12px;
  color: map-get($theme-colors, 'gray03') !important;
  top: -18px;
}

.select-icon {
  top: 2px !important;
  right: 6px !important;
}
</style>
